import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Settings, ArrowRight, ArrowLeft, CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

const transformations = () => {
  const navigate = useNavigate();

  const benefits = [
    "Comprehensive project management from start to finish",
    "Expert change management and stakeholder engagement",
    "Rigorous quality assurance processes",
    "Continuous performance monitoring and optimization",
    "Risk mitigation and contingency planning",
    "Knowledge transfer and team enablement",
  ];

  const transformationsServices = [
    {
    title: "Operating Model Redesign",
    description: "Reshape structures, governance, and decision-making to enable agility and alignment.",
  },
  {
    title: "Ways of Working Transformation",
    description: "Implement modern frameworks and collaboration models that enhance efficiency and team engagement.",
  },
  {
    title: "Change Leadership & Enablement",
    description: "Equip leaders and teams with the mindset, skills, and tools to drive and sustain transformation.",
  },
  {
    title: "Strategic Framework Implementation",
    description: "Adopt and tailor proven methodologies—such as Agile, Lean, or hybrid models—to your organizational context.",
  },
];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-background/95 backdrop-blur-sm border-b border-border z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate("/")}
              className="text-2xl font-vailery text-primary hover:opacity-80 transition-opacity"
            >
              Orren Consultancy
            </button>
            <Button variant="outline" onClick={() => navigate("/")} className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-hero relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-hero opacity-90"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="w-20 h-20 mx-auto mb-6 bg-white/10 rounded-lg flex items-center justify-center backdrop-blur-sm">
              <Settings className="h-10 w-10 text-white" />
            </div>
            <Badge variant="secondary" className="mb-6 bg-white/10 text-white border-white/20">
              Seamless Execution
            </Badge>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 animate-fade-in">Transformations</h1>
            <p className="text-xl md:text-2xl mb-8 text-white/90 animate-slide-up">
              Redefining your organization’s ways of working and operating models to drive sustainable performance and
              value delivery.{" "}
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-center text-foreground">Transformations Excellence</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <Card key={index} className="border-border/50 bg-card/80 backdrop-blur-sm">
                  <CardContent className="p-6 flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-accent mt-1 flex-shrink-0" />
                    <p className="text-muted-foreground">{benefit}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-secondary/30">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-center text-foreground">Transformations Services</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {transformationsServices.map((service, index) => (
                <Card
                  key={index}
                  className="border-border/50 bg-card/80 backdrop-blur-sm hover:shadow-medium transition-all"
                >
                  <CardHeader>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                    <CardDescription className="text-base">{service.description}</CardDescription>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-secondary">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6 text-foreground">Execute Your Vision Flawlessly</h2>
            <p className="text-xl text-muted-foreground mb-8">
              Partner with us to ensure successful implementation of your initiatives
            </p>
            <Button
              size="lg"
              className="bg-gradient-primary shadow-medium"
              onClick={() => navigate("/#book-consultation")}
            >
              Book Free Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-primary text-primary-foreground">
        <div className="container mx-auto px-6">
          <div className="text-center">
            <div className="text-3xl font-vailery mb-4">Orren</div>
            <p className="text-primary-foreground/80 mb-6">Professional Consulting Services</p>
            <div className="text-sm text-primary-foreground/60">© 2025 Orren. All rights reserved.</div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default transformations;
